/* eslint-disable */
import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import dates from '../../utils/dates';
import { getSlotAtX, pointInBox } from '../../utils/selection';

import { eventSegments } from '../../utils/eventLevels';
import Selection, { getBoundsForNode } from '../../Selection';
import EventRow from '../../EventRow';
import { dragAccessors } from './common';

const propTypes = {};

const eventTimes = (event, accessors) => {
    const start = accessors.start(event);
    let end = accessors.end(event);

    const isZeroDuration =
        dates.eq(start, end, 'minutes') && start.getMinutes() === 0;
    // make zero duration midnight events at least one day long
    if (isZeroDuration) end = dates.add(end, 1, 'day');
    return { start, end };
};

class WeekWrapper extends React.Component {
    static propTypes = {
        isAllDay: PropTypes.bool,
        slotMetrics: PropTypes.object.isRequired,
        accessors: PropTypes.object.isRequired,
        getters: PropTypes.object.isRequired,
        components: PropTypes.object.isRequired,
        resourceId: PropTypes.any,
    };

    static contextTypes = {
        draggable: PropTypes.shape({
            onStart: PropTypes.func,
            onEnd: PropTypes.func,
            // eslint-disable-next-line react/forbid-prop-types
            dragAndDropAction: PropTypes.object,
            onBeginAction: PropTypes.func,
        }),
    };

    constructor(...args) {
        super(...args);
        this.state = {};
    }

    componentDidMount() {
        this._selectable();
    }

    componentWillUnmount() {
        this._teardownSelectable();
    }

    // eslint-disable-next-line react/sort-comp
    reset() {
        if (this.state.segment) this.setState({ segment: null });
    }

    update(event, start, end) {
        const segment = eventSegments(
            { ...event, end, start, __isPreview: true },
            // eslint-disable-next-line react/prop-types
            this.props.slotMetrics.range,
            dragAccessors,
        );

        const { segment: lastSegment } = this.state;
        if (
            lastSegment &&
            segment.span === lastSegment.span &&
            segment.left === lastSegment.left &&
            segment.right === lastSegment.right
        ) {
            return;
        }
        this.setState({ segment });
    }

    handleMove = ({ x, y }, node) => {
        const { event } = this.context.draggable.dragAndDropAction;
        // eslint-disable-next-line react/prop-types
        const metrics = this.props.slotMetrics;
        // eslint-disable-next-line react/prop-types
        const { accessors } = this.props;

        if (!event) return;

        const rowBox = getBoundsForNode(node);

        if (!pointInBox(rowBox, { x, y })) {
            this.reset();
            return;
        }

        // Make sure to maintain the time of the start date while moving it to the new slot
        const start = dates.merge(
            // eslint-disable-next-line react/prop-types
            metrics.getDateForSlot(getSlotAtX(rowBox, x, false, metrics.slots)),
            // eslint-disable-next-line react/prop-types
            accessors.start(event),
        );

        const end = dates.add(
            start,
            // eslint-disable-next-line react/prop-types
            dates.diff(accessors.start(event), accessors.end(event), 'minutes'),
            'minutes',
        );

        this.update(event, start, end);
    };

    handleResize(point, node) {
        const { event, direction } = this.context.draggable.dragAndDropAction;
        // eslint-disable-next-line react/prop-types
        const { accessors, slotMetrics: metrics } = this.props;

        let { start, end } = eventTimes(event, accessors);

        const rowBox = getBoundsForNode(node);
        const cursorInRow = pointInBox(rowBox, point);

        if (direction === 'RIGHT') {
            if (cursorInRow) {
                if (metrics.last < start) return this.reset();
                // add min
                end = dates.add(
                    metrics.getDateForSlot(
                        getSlotAtX(rowBox, point.x, false, metrics.slots),
                    ),
                    1,
                    'day',
                );
            } else if (
                dates.inRange(start, metrics.first, metrics.last) ||
                (rowBox.bottom < point.y && +metrics.first > +start)
            ) {
                end = dates.add(metrics.last, 1, 'milliseconds');
            } else {
                this.setState({ segment: null });
                // eslint-disable-next-line consistent-return
                return;
            }

            end = dates.max(end, dates.add(start, 1, 'day'));
        } else if (direction === 'LEFT') {
            // inbetween Row
            if (cursorInRow) {
                if (metrics.first > end) return this.reset();

                start = metrics.getDateForSlot(
                    getSlotAtX(rowBox, point.x, false, metrics.slots),
                );
            } else if (
                dates.inRange(end, metrics.first, metrics.last) ||
                (rowBox.top > point.y && +metrics.last < +end)
            ) {
                start = dates.add(metrics.first, -1, 'milliseconds');
            } else {
                this.reset();
                // eslint-disable-next-line consistent-return
                return;
            }

            start = dates.min(dates.add(end, -1, 'day'), start);
        }

        this.update(event, start, end);
    }

    _selectable = () => {
        // eslint-disable-next-line react/no-find-dom-node
        const node = findDOMNode(this).closest(
            '.rbc-month-row, .rbc-allday-cell',
        );
        const container = node.closest('.rbc-month-view, .rbc-time-view');

        // eslint-disable-next-line no-multi-assign
        const selector = (this._selector = new Selection(() => container));

        selector.on('beforeSelect', point => {
            // eslint-disable-next-line react/prop-types
            const { isAllDay } = this.props;
            const { action } = this.context.draggable.dragAndDropAction;

            return (
                action === 'move' ||
                (action === 'resize' &&
                    (!isAllDay || pointInBox(getBoundsForNode(node), point)))
            );
        });

        selector.on('selecting', box => {
            const bounds = getBoundsForNode(node);
            const { dragAndDropAction } = this.context.draggable;

            if (dragAndDropAction.action === 'move')
                this.handleMove(box, bounds);
            if (dragAndDropAction.action === 'resize')
                this.handleResize(box, bounds);
        });

        selector.on('selectStart', () => this.context.draggable.onStart());
        selector.on('select', point => {
            const bounds = getBoundsForNode(node);

            if (!this.state.segment || !pointInBox(bounds, point)) return;
            this.handleInteractionEnd();
        });
        selector.on('click', () => this.context.draggable.onEnd(null));
    };

    handleInteractionEnd = () => {
        // eslint-disable-next-line react/prop-types
        const { resourceId, isAllDay } = this.props;
        const { event } = this.state.segment;

        this.reset();

        this.context.draggable.onEnd({
            start: event.start,
            end: event.end,
            resourceId,
            isAllDay,
        });
    };

    _teardownSelectable = () => {
        if (!this._selector) return;
        this._selector.teardown();
        this._selector = null;
    };

    render() {
        // eslint-disable-next-line react/prop-types
        const { children, accessors } = this.props;

        const { segment } = this.state;

        return (
            <div className="rbc-addons-dnd-row-body">
                {children}

                {segment && (
                    <EventRow
                        {...this.props}
                        selected={null}
                        className="rbc-addons-dnd-drag-row"
                        segments={[segment]}
                        accessors={{
                            ...accessors,
                            ...dragAccessors,
                        }}
                    />
                )}
            </div>
        );
    }
}

WeekWrapper.propTypes = propTypes;

export default WeekWrapper;
